import React, { useEffect, useMemo, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { formatDate } from "@/lib/formatDate";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, FileSignature, FileDown, Play } from "lucide-react";
import { toast } from "sonner";

/**
 * LSV-Mandate Management — eine pro (Kunde + IBAN).
 * Mandate sind die Voraussetzung für pain.008 Lastschrift-Einzüge.
 */
export default function LsvMandates() {
  const companyId = localStorage.getItem("demoCompanyId");
  const [mandates, setMandates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    customer_id: "",
    debtor_name: "",
    debtor_iban: "",
    signed_date: new Date().toISOString().slice(0, 10),
    description: "",
    mandate_id: "",
    recurrence: "",
    next_collection_date: "",
    default_amount: "",
    invoice_description: "",
  });

  const loadAll = async () => {
    try {
      const [m, c] = await Promise.all([
        apiClient.get(`/companies/${companyId}/lsv-mandates`),
        apiClient.get(`/companies/${companyId}/contacts`, { params: { type: "customer" } }),
      ]);
      setMandates(m.data || []);
      setCustomers(c.data || []);
    } catch (e) {
      toast.error("Mandate konnten nicht geladen werden.");
    }
  };

  useEffect(() => {
    if (companyId) loadAll();
  }, [companyId]);

  const handleSelectCustomer = (customer_id) => {
    const c = customers.find((x) => x._id === customer_id);
    setForm((p) => ({
      ...p,
      customer_id,
      debtor_name: c?.name || "",
      debtor_iban: (c?.iban || "").replace(/\s+/g, "").toUpperCase(),
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.debtor_iban || !form.signed_date) {
      toast.error("Kunde, IBAN und Unterschriftsdatum sind Pflicht.");
      return;
    }
    setCreating(true);
    try {
      await apiClient.post(`/companies/${companyId}/lsv-mandates`, {
        customer_id: form.customer_id,
        debtor_name: form.debtor_name.trim(),
        debtor_iban: form.debtor_iban.replace(/\s+/g, "").toUpperCase(),
        signed_date: form.signed_date,
        description: form.description || null,
        mandate_id: form.mandate_id || null,
        recurrence: form.recurrence || null,
        next_collection_date: form.next_collection_date || null,
        default_amount: form.default_amount ? parseFloat(form.default_amount) : null,
        invoice_description: form.invoice_description || null,
      });
      toast.success("Mandat erstellt.");
      setForm({
        customer_id: "",
        debtor_name: "",
        debtor_iban: "",
        signed_date: new Date().toISOString().slice(0, 10),
        description: "",
        mandate_id: "",
        recurrence: "",
        next_collection_date: "",
        default_amount: "",
        invoice_description: "",
      });
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Erstellen.");
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async (m) => {
    try {
      await apiClient.patch(`/companies/${companyId}/lsv-mandates/${m.id}`, { status: "cancelled" });
      toast.success("Mandat widerrufen.");
      await loadAll();
    } catch (e) {
      toast.error("Konnte nicht widerrufen werden.");
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Mandat ${m.mandate_id} endgültig löschen?`)) return;
    try {
      await apiClient.delete(`/companies/${companyId}/lsv-mandates/${m.id}`);
      toast.success("Mandat gelöscht.");
      await loadAll();
    } catch (e) {
      toast.error("Konnte nicht gelöscht werden.");
    }
  };

  const handleDownloadPdf = async (m) => {
    try {
      const res = await apiClient.get(
        `/companies/${companyId}/lsv-mandates/${m.id}/pdf`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Mandat_${m.mandate_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF konnte nicht erzeugt werden.");
    }
  };

  const handleRunSubscriptions = async () => {
    try {
      const res = await apiClient.post("/scheduler/subscriptions/run-now");
      const { invoices_created, mandates_advanced, errors } = res.data;
      if (errors?.length) {
        toast.warning(
          `${invoices_created} Auto-Rechnungen erstellt · ${errors.length} Mandate-Fehler`,
        );
      } else if (invoices_created > 0) {
        toast.success(
          `${invoices_created} Auto-Rechnungen aus ${mandates_advanced} fälligen Mandaten erstellt.`,
        );
      } else {
        toast.info("Keine fälligen Mandate gefunden.");
      }
      await loadAll();
    } catch (e) {
      toast.error("Subscription-Run fehlgeschlagen.");
    }
  };

  const activeCount = useMemo(
    () => mandates.filter((m) => m.status === "active").length,
    [mandates],
  );

  return (
    <div className="space-y-6" data-testid="lsv-mandates-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2">
            <FileSignature className="h-7 w-7 text-emerald-600" />
            LSV-Mandate
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Schweizer Lastschriftverfahren (CH-DD): {activeCount} aktive Mandate.
            Voraussetzung für automatische Einzüge via pain.008.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-full"
          onClick={handleRunSubscriptions}
          data-testid="lsv-run-subscriptions-button"
          title="Erzeugt Auto-Rechnungen für alle aktiven Mandate, deren next_collection_date erreicht ist."
        >
          <Play className="h-4 w-4 mr-1" />
          Auto-Rechnungen jetzt erstellen
        </Button>
      </div>

      <Card className="p-5 max-w-2xl border-slate-200 shadow-sm" data-testid="lsv-mandate-form-card">
        <form onSubmit={handleCreate} className="space-y-3">
          <h2 className="text-base font-semibold flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> Neues Mandat
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1 md:col-span-2">
              <Label>Kunde</Label>
              <Select value={form.customer_id} onValueChange={handleSelectCustomer}>
                <SelectTrigger data-testid="lsv-mandate-customer-select">
                  <SelectValue placeholder="Kunde wählen …" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c._id} value={c._id}>
                      {c.name}{c.iban ? ` · ${c.iban}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Zahler-Name</Label>
              <Input
                value={form.debtor_name}
                onChange={(e) => setForm({ ...form, debtor_name: e.target.value })}
                data-testid="lsv-mandate-debtor-name"
              />
            </div>
            <div className="space-y-1">
              <Label>IBAN</Label>
              <Input
                value={form.debtor_iban}
                onChange={(e) => setForm({ ...form, debtor_iban: e.target.value })}
                placeholder="CH00 0000 ..."
                data-testid="lsv-mandate-debtor-iban"
              />
            </div>
            <div className="space-y-1">
              <Label>Unterschriftsdatum</Label>
              <SwissDateField
                
                value={form.signed_date}
                onChange={(e) => setForm({ ...form, signed_date: e.target.value })}
                data-testid="lsv-mandate-signed-date"
              />
            </div>
            <div className="space-y-1">
              <Label>Mandate-ID (optional)</Label>
              <Input
                value={form.mandate_id}
                onChange={(e) => setForm({ ...form, mandate_id: e.target.value })}
                placeholder="auto-generiert wenn leer"
                data-testid="lsv-mandate-id-input"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Beschreibung</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="z.B. Monats-Abo Cloud-Service"
                data-testid="lsv-mandate-description"
              />
            </div>
          </div>

          {/* Subscription / Auto-Recurring */}
          <div className="border-t pt-3 mt-2">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">
              Wiederkehrend (optional)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Frequenz</Label>
                <Select
                  value={form.recurrence}
                  onValueChange={(v) => setForm({ ...form, recurrence: v })}
                >
                  <SelectTrigger data-testid="lsv-mandate-recurrence-select">
                    <SelectValue placeholder="Manuell (kein Auto)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monatlich</SelectItem>
                    <SelectItem value="quarterly">Quartalsweise</SelectItem>
                    <SelectItem value="yearly">Jährlich</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Nächste Fälligkeit</Label>
                <SwissDateField
                  
                  value={form.next_collection_date}
                  onChange={(e) => setForm({ ...form, next_collection_date: e.target.value })}
                  data-testid="lsv-mandate-next-date"
                />
              </div>
              <div className="space-y-1">
                <Label>Betrag CHF</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.default_amount}
                  onChange={(e) => setForm({ ...form, default_amount: e.target.value })}
                  placeholder="z.B. 99.00"
                  data-testid="lsv-mandate-default-amount"
                />
              </div>
              <div className="space-y-1 md:col-span-3">
                <Label>Rechnungstext (auf Auto-Invoice)</Label>
                <Input
                  value={form.invoice_description}
                  onChange={(e) => setForm({ ...form, invoice_description: e.target.value })}
                  placeholder="z.B. Cloud-Service Abo"
                  data-testid="lsv-mandate-invoice-description"
                />
              </div>
            </div>
          </div>
          <Button
            type="submit"
            size="sm"
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={creating}
            data-testid="lsv-mandate-submit-button"
          >
            {creating ? "Erstelle …" : "Mandat erstellen"}
          </Button>
        </form>
      </Card>

      <Card className="p-4 border-slate-200 shadow-sm">
        <Table data-testid="lsv-mandates-table">
          <TableHeader>
            <TableRow>
              <TableHead>Mandate-ID</TableHead>
              <TableHead>Kunde</TableHead>
              <TableHead>IBAN</TableHead>
              <TableHead>Unterschrift</TableHead>
              <TableHead>Wiederkehrend</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mandates.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-6">
                  Noch keine Mandate vorhanden.
                </TableCell>
              </TableRow>
            )}
            {mandates.map((m) => (
              <TableRow key={m.id} data-testid={`lsv-mandate-row-${m.id}`}>
                <TableCell className="font-mono text-xs">{m.mandate_id}</TableCell>
                <TableCell>{m.customer_name || m.debtor_name}</TableCell>
                <TableCell className="font-mono text-xs">{m.debtor_iban}</TableCell>
                <TableCell>{formatDate(m.signed_date)}</TableCell>
                <TableCell>
                  {m.recurrence ? (
                    <div className="text-xs">
                      <span className="font-medium">
                        {{
                          monthly: "Monatlich",
                          quarterly: "Quartalsweise",
                          yearly: "Jährlich",
                        }[m.recurrence] || m.recurrence}
                      </span>
                      {m.default_amount && (
                        <span className="text-slate-500"> · {m.default_amount.toFixed(2)} CHF</span>
                      )}
                      {m.next_collection_date && (
                        <div className="text-[10px] text-slate-500">
                          Nächste: {formatDate(m.next_collection_date)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">manuell</span>
                  )}
                </TableCell>
                <TableCell>
                  {m.status === "active" ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">aktiv</Badge>
                  ) : m.status === "paused" ? (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">pausiert</Badge>
                  ) : (
                    <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">widerrufen</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button
                    type="button" size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => handleDownloadPdf(m)}
                    data-testid={`lsv-mandate-pdf-${m.id}`}
                    title="Mandat als PDF herunterladen"
                  >
                    <FileDown className="h-3.5 w-3.5 text-emerald-700" />
                  </Button>
                  {m.status === "active" && (
                    <Button
                      type="button" size="sm" variant="ghost" className="h-7 text-xs"
                      onClick={() => handleCancel(m)}
                      data-testid={`lsv-mandate-cancel-${m.id}`}
                    >
                      Widerrufen
                    </Button>
                  )}
                  <Button
                    type="button" size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => handleDelete(m)}
                    data-testid={`lsv-mandate-delete-${m.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
